"use client";

import type { CSSProperties } from "react";

export interface GlassSwitchProps {
	readonly checked: boolean;
	readonly onChange: (checked: boolean) => void;
	readonly label: string;
	readonly disabled?: boolean;
	readonly className?: string;
}

/**
 * GlassSwitch — 50×30 capsule track with a 26px thumb. Mirrors the iOS
 * `Toggle` control tinted with `DSColor.accent`. Track dims while unchecked
 * and turns accent when checked. All colors resolve through DS CSS vars.
 *
 * Contract (native-design-system-v1.json v1.1.0):
 *   switch.trackWidthDp = 50, switch.trackHeightDp = 30,
 *   switch.thumbDp = 26, switch.thumbInsetDp = 2,
 *   switch.tapPaddingDp = 7, switch.checkedAccent = true,
 *   switch.radiusToken = full
 */
export function GlassSwitch({
	checked,
	onChange,
	label,
	disabled = false,
	className = "",
}: Readonly<GlassSwitchProps>) {
	// The button is the tap target (track + 7px tap padding on every side,
	// pulled back with negative margin so the layout footprint stays 50×30).
	const buttonStyle: CSSProperties = {
		padding: "7px",
		margin: "-7px",
		background: "transparent",
		border: 0,
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.45 : 1,
		flex: "0 0 auto",
		boxSizing: "content-box",
	};
	const trackStyle: CSSProperties = {
		display: "block",
		width: "50px",
		height: "30px",
		borderRadius: "9999px",
		border: "1px solid var(--ds-glass-stroke)",
		background: checked
			? "var(--ds-accent)"
			: "color-mix(in display-p3, var(--ds-text-primary) 18%, var(--ds-surface))",
		boxShadow: "inset 0 1px 0 rgb(255 255 255 / 0.06)",
		position: "relative",
		transition: "background 180ms cubic-bezier(0.4, 0, 0.2, 1)",
	};
	const thumbStyle: CSSProperties = {
		position: "absolute",
		top: "2px",
		left: checked ? "22px" : "2px",
		width: "26px",
		height: "26px",
		borderRadius: "9999px",
		background: "var(--ds-on-media-primary)",
		boxShadow: "0 2px 6px rgb(0 0 0 / 0.32)",
		transition: "left 180ms cubic-bezier(0.22, 1, 0.36, 1)",
	};
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-label={label}
			className={`ds-switch ${className}`.trim()}
			style={buttonStyle}
			disabled={disabled}
			onClick={() => {
				if (!disabled) onChange(!checked);
			}}
		>
			<span style={trackStyle} aria-hidden="true">
				<span style={thumbStyle} />
			</span>
		</button>
	);
}
